import { Flex } from 'components';
import { TableCell, TableHeader } from './AuditFormsList.styled';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteAuditForm, getAuditForms } from 'api/audits';
import { IAuditForm } from 'types/audits';
import { parseItemsToMethodologyTree } from 'utilities/practices';
import { ActiveDot } from 'components/ActiveDot/ActiveDot';
import { P } from 'components/Typography/Typography';
import { Button, Checkbox } from 'components/_form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faCirclePlus,
  faEdit,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { DeleteAuditFormModal } from './DeleteAuditFormModal';
import { toast } from 'react-toastify';
import { showRequestError } from 'utilities/showRequestError';
import { AuditFormCodeBlock } from '../AuditFormForm/components/AuditFormCodeNameField/AuditFormCodeBlock';
import styled from 'styled-components';

const MethodologyIcon = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: 8px;
`;

const MethodologyIconFallback = styled(Flex)`
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.neutral.tableHeader};
  font-weight: 700;
  font-size: 12px;
  text-align: center;
  padding: 4px;
`;

const PracticeName = styled.div`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.neutral.greyButton};
  line-height: 1.3;
`;

export const AuditFormsList = () => {
  const [forms, setForms] = useState<IAuditForm[]>([]);
  const [statesFilter, setStatesFilter] = useState<string[]>([]);
  const [deletingAuditFormId, setDeletingAuditFormId] = useState<number>();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const formsMethodologiesStructure = useMemo(
    () => parseItemsToMethodologyTree(forms),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(forms)]
  );

  const handleGetAuditForms = useCallback(async () => {
    const auditFormsRes = await getAuditForms({
      filters: { states: statesFilter }
    });

    if ('error' in auditFormsRes) {
      return;
    }

    setForms(auditFormsRes.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(statesFilter)]);

  useEffect(() => {
    handleGetAuditForms();
  }, [handleGetAuditForms]);

  const handleDeleteAuditForm = async () => {
    if (!deletingAuditFormId) return;

    const deleteAuditFormRes = await deleteAuditForm(deletingAuditFormId);

    if (deleteAuditFormRes && 'error' in deleteAuditFormRes) {
      showRequestError(deleteAuditFormRes.error, t);
      return;
    }

    toast.success(t('common.success'));
    setDeletingAuditFormId(undefined);
    handleGetAuditForms();
  };

  const renderFormRow = (auditForm: IAuditForm) => (
    <Flex key={`formsMethodologiesStructure-audit-${auditForm.id}`}>
      <TableCell width="10%" justifyContent="center">
        <ActiveDot active={auditForm.state === 'active'} />
      </TableCell>
      <TableCell width="30%" gap="10px">
        <AuditFormCodeBlock
          codeId={auditForm.id_code}
          colorCode={auditForm.color_code}
        />
        <P variant="body">{auditForm.name}</P>
      </TableCell>
      <TableCell width="10%">-</TableCell>
      <TableCell width="30%">
        {auditForm.areas.map(({ name }) => name).join(', ')}
      </TableCell>
      <TableCell width="10%" gap="5px">
        <Button
          variant="eucalyptus"
          icon={<FontAwesomeIcon icon={faEdit} size="xl" />}
          onClick={() => {
            navigate(
              `/app/administration/audits/edit-audit-form/${auditForm.id}`
            );
          }}
        />
        <Button
          variant="redWhite"
          bordered
          transparent
          icon={<FontAwesomeIcon icon={faTrashCan} size="xl" />}
          onClick={() => {
            setDeletingAuditFormId(auditForm.id);
          }}
        />
        <Flex flexDirection="column">
          <Button
            bordered
            transparent
            icon={<FontAwesomeIcon icon={faChevronUp} size="xl" />}
            minHeight="17px"
          />
          <Button
            bordered
            transparent
            icon={<FontAwesomeIcon icon={faChevronDown} size="xl" />}
            minHeight="17px"
          />
        </Flex>
      </TableCell>
    </Flex>
  );

  return (
    <Flex flexDirection="column">
      <Flex
        my={2}
        width="100%"
        justifyContent="end"
        alignItems="center"
        gap="25px"
        flexDirection={['column', 'row']}
      >
        <Flex alignItems="center" gap="15px" flexDirection={['column', 'row']}>
          <P variant="h4">{t('common.show')}:</P>
          <Checkbox
            label={t('administrationAuditsView.inactive')}
            checked={statesFilter.includes('inactive')}
            onChange={(e) => {
              setStatesFilter((prevStatesFilter) =>
                e.target.checked
                  ? [...prevStatesFilter, 'inactive']
                  : prevStatesFilter.filter((state) => state !== 'inactive')
              );
            }}
          />
          <Checkbox
            label={t('administrationAuditsView.archived')}
            checked={statesFilter.includes('archived')}
            onChange={(e) => {
              setStatesFilter((prevStatesFilter) =>
                e.target.checked
                  ? [...prevStatesFilter, 'archived']
                  : prevStatesFilter.filter((state) => state !== 'archived')
              );
            }}
          />
        </Flex>
        <Button
          variant="eucalyptus"
          onClick={() => {
            navigate('add-audit-form');
          }}
        >
          <Flex alignItems="center" gap="5px">
            <FontAwesomeIcon icon={faCirclePlus} />
            {t('common.add')}
          </Flex>
        </Button>
      </Flex>

      <Flex overflow="auto" width="100%">
        <Flex flexDirection="column" minWidth="1200px" width="100%">
          <Flex>
            <TableHeader width="10%"></TableHeader>
            <TableHeader width="10%">
              {t('administrationAuditsView.tableHeaders.active')}
            </TableHeader>
            <TableHeader width="30%">
              {t('administrationAuditsView.tableHeaders.practice/area/form')}
            </TableHeader>
            <TableHeader width="10%">
              {t('administrationAuditsView.tableHeaders.version')}
            </TableHeader>
            <TableHeader width="30%">
              {t('administrationAuditsView.tableHeaders.usedInAreas')}
            </TableHeader>
            <TableHeader width="10%"></TableHeader>
          </Flex>

          {formsMethodologiesStructure.map((methodology) => (
            <Flex
              minWidth="100%"
              key={`formsMethodologiesStructure-methodology-${methodology.id}`}
              overflow="hidden"
              alignItems="flex-start"
            >
              <Flex
                minWidth="10%"
                p={2}
                alignItems="flex-start"
                justifyContent="center"
              >
                {methodology.image ? (
                  <MethodologyIcon
                    src={`${process.env.REACT_APP_API_URL}${methodology.image}`}
                    alt={methodology.name}
                    title={methodology.name}
                  />
                ) : (
                  <MethodologyIconFallback title={methodology.name}>
                    {methodology.name}
                  </MethodologyIconFallback>
                )}
              </Flex>
              <Flex minWidth="90%" flexDirection="column" gap="8px" pt="50px">
                {(methodology.directItems || []).map(renderFormRow)}

                {methodology.practices.map((practice) => (
                  <Flex
                    key={`formsMethodologiesStructure-practice-${practice.id}`}
                    flexDirection="column"
                    gap="8px"
                  >
                    <Flex alignItems="center" py={1}>
                      <Flex width="10%" flexShrink={0} />
                      <PracticeName>
                        {practice.name || practice.id_code || `#${practice.id}`}
                      </PracticeName>
                    </Flex>
                    {practice.items.map(renderFormRow)}
                  </Flex>
                ))}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>

      <DeleteAuditFormModal
        isOpen={!!deletingAuditFormId}
        onCancelClick={() => {
          setDeletingAuditFormId(undefined);
        }}
        onSubmit={handleDeleteAuditForm}
      />
    </Flex>
  );
};
